import { Controller } from 'stimulus'
import slugify from 'slugify'
import h from 'hyperscript'
import { BREAKPOINTS } from '../../_js/base/consts'
import mediaFrom from '../../_js/utils/media-from'

// Minimum number of headings required to show the TOC
const MIN_HEADINGS = 2

// Defaults for controller values
const DEFAULT_DEPTH = 1
const DEFAULT_START_LEVEL = 2

const CSS_CLASSES = {
  ITEM: 'on-this-page__item',
  LINK: 'on-this-page__link',
  LIST: 'on-this-page__list',
  HIDDEN: 'is-hidden',
  LEAD: 'lead',
}

export default class OnThisPage extends Controller {
  static targets = ['wrapper', 'list']

  static values = {
    depth: Number,
    startLevel: Number,
  }

  connect() {
    // Only initialize above VIEWPORT_L
    if (!mediaFrom(BREAKPOINTS.VIEWPORT_L)) {
      return
    }

    if (!this.hasDepthValue) {
      this.depthValue = DEFAULT_DEPTH
    }
    if (!this.hasStartLevelValue) {
      this.startLevelValue = DEFAULT_START_LEVEL
    }

    // No point proceeding without a list or valid depth
    if (!this.hasListTarget || this.depthValue < 1) {
      return
    }

    // 'h2, h3, ...' based on start level and depth
    const headingSelector = Array.from({ length: this.depthValue }, (_, i) => `h${this.startLevelValue + i}`).join(', ')
    const headings = Array.from(this.element.querySelectorAll(headingSelector))

    if (headings.length < MIN_HEADINGS) {
      return
    }

    this.buildTocStructure(headings)

    // Position the TOC after the lead paragraph and show it
    if (this.hasWrapperTarget) {
      const leadParagraph = document.querySelector(`.${CSS_CLASSES.LEAD}`)
      if (leadParagraph) {
        leadParagraph.after(this.wrapperTarget)
      }
      this.wrapperTarget.classList.remove(CSS_CLASSES.HIDDEN)
    }
  }

  buildTocStructure(headings) {
    const rootList = this.listTarget

    // Map tracks the most recent heading at each level
    // Key: heading level, Value: the latest <li> element at that level
    // This allows us to find the correct parent when nesting deeper headings
    const latestHeadingAtLevel = new Map()

    headings.forEach((heading) => {
      const headingLevel = parseInt(heading.tagName.charAt(1))

      // Ensure heading has an ID
      if (!heading.id) {
        heading.id = slugify(heading.textContent)
      }

      // Create TOC item
      const tocItem = h(
        `li.${CSS_CLASSES.ITEM}`,
        h(`a.${CSS_CLASSES.LINK}`, { href: `#${heading.id}` }, heading.textContent)
      )

      if (headingLevel === this.startLevelValue) {
        // Start level: Always goes at root level
        rootList.appendChild(tocItem)
        // Remember this heading as the current parent for deeper headings
        latestHeadingAtLevel.set(this.startLevelValue, tocItem)
        // Clear any deeper level parents since we're starting a new section
        for (let level = this.startLevelValue + 1; level <= 6; level++) {
          latestHeadingAtLevel.delete(level)
        }
      } else {
        // Deeper headings: Find the appropriate parent heading
        let currentParent = null

        // Look backwards for the closest parent (allows for skipping levels, e.g. h2 -> h4)
        for (let parentLevel = headingLevel - 1; parentLevel >= this.startLevelValue; parentLevel--) {
          if (latestHeadingAtLevel.has(parentLevel)) {
            currentParent = latestHeadingAtLevel.get(parentLevel)
            break
          }
        }

        if (currentParent) {
          // Find or create nested list under the parent item
          let nestedList = currentParent.querySelector(`.${CSS_CLASSES.LIST}`)
          if (!nestedList) {
            nestedList = h(`ul.${CSS_CLASSES.LIST}`)
            currentParent.appendChild(nestedList)
          }
          nestedList.appendChild(tocItem)

          // Remember this item as the latest heading at this level
          latestHeadingAtLevel.set(headingLevel, tocItem)
          // Clear any deeper level parents since we're at a new section
          for (let level = headingLevel + 1; level <= 6; level++) {
            latestHeadingAtLevel.delete(level)
          }
        } else {
          // No valid parent found, add to root level as fallback
          rootList.appendChild(tocItem)
          latestHeadingAtLevel.set(headingLevel, tocItem)
        }
      }
    })
  }
}
